/**
 * Product Badges — reads #badge-config JSON and hydrates all
 * .product-badges-global containers on the page.
 *
 * Handles:
 *  - Sold-out badges (label + color scheme or custom colors)
 *  - Sale badges (template interpolation + color scheme or custom colors)
 *  - Tag badges (match product tags → label + color scheme or custom colors)
 *  - Timer badges (match product tags → label + countdown + color scheme or custom colors)
 *
 * Styling (border-radius, padding, gap, border) is handled by CSS variables
 * set in the badges section's <style> tag — not inline styles.
 */
(function () {
  'use strict';

  // Temporary Vasky pause: restore these availability badges by setting false.
  var hideAvailabilityBadges = true;

  /* ------------------------------------------------------------------ */
  /*  Helpers                                                            */
  /* ------------------------------------------------------------------ */

  function applyColorScheme(el, scheme) {
    if (scheme) el.classList.add('color-' + scheme);
  }

  function applyCustomColors(el, badgeDef) {
    if (badgeDef.customColors && badgeDef.bgColor && badgeDef.contentColor) {
      el.setAttribute('data-custom-colors', '');
      el.style.setProperty('--badge-bg', badgeDef.bgColor);
      el.style.setProperty('--badge-content', badgeDef.contentColor);
    } else {
      applyColorScheme(el, badgeDef.colorScheme);
    }
  }

  /**
   * Format a time-remaining into a human-readable countdown string.
   */
  function formatCountdown(ms, format) {
    if (ms <= 0) return '';
    var totalMinutes = Math.floor(ms / 60000);
    var totalHours = Math.floor(totalMinutes / 60);
    var days = Math.floor(totalHours / 24);
    var hours = totalHours % 24;
    var minutes = totalMinutes % 60;

    switch (format) {
      case 'hours':
        return totalHours + 'h';
      case 'full':
        var parts = [];
        if (days > 0) parts.push(days + 'd');
        if (hours > 0 || days > 0) parts.push(hours + 'h');
        parts.push(minutes + 'm');
        return parts.join(' ');
      case 'days':
      default:
        if (days > 0) return days + 'd ' + hours + 'h';
        return hours + 'h ' + minutes + 'm';
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Hydrate                                                            */
  /* ------------------------------------------------------------------ */

  var timerBadgeEls = [];

  function hydrate() {
    var all = document.querySelectorAll('.product-badges-global');
    if (all.length > 0) hydrateContainers(Array.prototype.slice.call(all));
  }

  /* ------------------------------------------------------------------ */
  /*  Hydrate a specific subset of containers (or all if none given)    */
  /* ------------------------------------------------------------------ */

  function hydrateContainers(containers) {
    var configEl = document.getElementById('badge-config');
    if (!configEl) return;

    var config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (e) {
      console.warn('[product-badges] Failed to parse badge config', e);
      return;
    }

    // Morphing can retain a container while replacing its children and attributes.
    // Ignore our own DOM writes so rehydration cannot trigger an observer loop.
    if (domObserver) domObserver.disconnect();
    timerBadgeEls = timerBadgeEls.filter(function (item) {
      return item.el.isConnected && !containers.some(function (c) { return c.contains(item.el); });
    });
    containers.forEach(function (container) {
      var tagsAttr = container.getAttribute('data-product-tags') || '';
      var productTags = tagsAttr
        .split(',')
        .map(function (t) { return t.trim(); })
        .filter(Boolean);

      container.classList.remove('product-badges-global--top-left', 'product-badges-global--top-right');
      container.classList.add('product-badges-global--' + config.position);

      var soldOutLabel = container.querySelector('[data-sold-out-badge]');
      if (soldOutLabel && hideAvailabilityBadges) {
        soldOutLabel.closest('.product-badges-global__badge').remove();
      } else if (soldOutLabel) {
        soldOutLabel.textContent = config.soldOut.label || 'Sold out';
        var soldOutBadge = soldOutLabel.closest('.product-badges-global__badge');
        applyCustomColors(soldOutBadge, config.soldOut);
      }

      var saleLabel = container.querySelector('[data-sale-badge]');
      if (saleLabel) {
        var saleBadge = saleLabel.closest('.product-badges-global__badge');
        var tpl = config.sale.template || 'SLEVA {amount_with_currency}';
        var discountAmount = saleBadge ? saleBadge.getAttribute('data-discount-amount') || '' : '';
        var percentageOff = saleBadge ? saleBadge.getAttribute('data-percentage-off') || '' : '';
        var text = tpl
          .replace('{amount_with_currency}', discountAmount)
          .replace('{percentage_off}', percentageOff);
        saleLabel.textContent = text;
        applyCustomColors(saleBadge, config.sale);
      }

      container.querySelectorAll('[data-badge-injected]').forEach(function (el) {
        el.remove();
      });

      if (config.badges && config.badges.length > 0) {
        config.badges.forEach(function (badgeDef) {
          if (!badgeDef.tag) return;
          var tagLower = badgeDef.tag.toLowerCase().trim();
          if (hideAvailabilityBadges && tagLower === 'info: skladem') return;
          if (productTags.indexOf(tagLower) === -1) return;

          var span = document.createElement('span');
          span.className = 'product-badges-global__badge';
          span.setAttribute('data-badge-injected', '');
          if (tagLower === 'badge:csr100') span.classList.add('csr-100-clock');
          applyCustomColors(span, badgeDef);

          var label = badgeDef.label || badgeDef.tag;

          if (badgeDef.type === 'timer-badge' && badgeDef.timerEndDate) {
            var endDate = new Date(badgeDef.timerEndDate);
            var now = Date.now();
            var remaining = endDate.getTime() - now;

            if (remaining <= 0) {
              if (badgeDef.timerExpiredAction === 'hide') return;
              var labelSpan = document.createElement('span');
              labelSpan.className = 'product-badges-global__label';
              labelSpan.textContent = label;
              span.appendChild(labelSpan);
            } else {
              var labelSpan2 = document.createElement('span');
              labelSpan2.className = 'product-badges-global__label';
              labelSpan2.textContent = label;
              span.appendChild(labelSpan2);
              span.appendChild(document.createTextNode(' '));
              var timerSpan = document.createElement('span');
              timerSpan.className = 'product-badges-global__timer';
              timerSpan.textContent = formatCountdown(remaining, badgeDef.timerFormat);
              span.appendChild(timerSpan);
              span.setAttribute('data-badge-type', 'timer');
              timerBadgeEls.push({
                el: timerSpan,
                endDate: endDate,
                format: badgeDef.timerFormat,
                expiredAction: badgeDef.timerExpiredAction,
                badge: span,
                label: label
              });
            }
          } else {
            var labelSpan3 = document.createElement('span');
            labelSpan3.className = 'product-badges-global__label';
            labelSpan3.textContent = label;
            span.appendChild(labelSpan3);
          }

          container.appendChild(span);
        });
      }
    });
    if (domObserver) observeBadges();
  }

  // Keep PDP sale badges in sync with the selected variant and its price.
  document.addEventListener('variant:update', function (event) {
    var data = event.detail && event.detail.data;
    if (!data || !data.html || !event.target.closest) return;
    var section = event.target.closest('.shopify-section, dialog');
    if (!section || !event.target.closest('variant-picker[data-template-product-match="true"]')) return;
    var productId = data.newProduct ? data.newProduct.id : data.productId;
    var source = Array.from(data.html.querySelectorAll('.product-badges-global[data-badge-context="page"]')).find(function (badge) {
      return badge.dataset.productId === String(productId);
    });
    if (!source) return;
    section.querySelectorAll('.product-badges-global[data-badge-context="page"]').forEach(function (current) {
      var replacement = source.cloneNode(true);
      current.replaceWith(replacement);
      hydrateContainers([replacement]);
    });
  });

  /* ------------------------------------------------------------------ */
  /*  Initial hydration                                                  */
  /* ------------------------------------------------------------------ */

  hydrate();

  /* ------------------------------------------------------------------ */
  /*  MutationObserver: hydrate newly added badge containers             */
  /* ------------------------------------------------------------------ */

  var pendingContainers = [];
  var rafPending = false;

  function flushPending() {
    rafPending = false;
    if (pendingContainers.length === 0) return;
    var toHydrate = Array.from(new Set(pendingContainers));
    pendingContainers = [];
    // Re-query from DOM so we get fresh nodes (morph may have patched attributes)
    var fresh = toHydrate.map(function (c) {
      return c.isConnected ? c : null;
    }).filter(Boolean);
    if (fresh.length > 0) hydrateContainers(fresh);
  }

  var domObserver = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      var target = mutation.target.nodeType === 1 ? mutation.target : mutation.target.parentElement;
      var retained = target && target.closest('.product-badges-global');
      if (retained) pendingContainers.push(retained);
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (node.classList && node.classList.contains('product-badges-global')) {
          pendingContainers.push(node);
        } else {
          node.querySelectorAll && node.querySelectorAll('.product-badges-global').forEach(function (c) {
            pendingContainers.push(c);
          });
        }
      });
    });
    if (pendingContainers.length > 0 && !rafPending) {
      rafPending = true;
      requestAnimationFrame(flushPending);
    }
  });

  function observeBadges() {
    domObserver.observe(document.body, {
      childList: true, subtree: true, characterData: true, attributes: true,
      attributeFilter: ['class', 'data-product-tags', 'data-product-id', 'data-percentage-off', 'data-discount-amount']
    });
  }
  observeBadges();

  /* ------------------------------------------------------------------ */
  /*  Theme editor: re-hydrate when the badges section re-renders        */
  /* ------------------------------------------------------------------ */

  if (Shopify && Shopify.designMode) {
    ['shopify:section:load', 'shopify:section:rerender',
     'shopify:block:select', 'shopify:block:deselect',
     'shopify:block:remove'].forEach(function (evt) {
      document.addEventListener(evt, function () {
        hydrate();
      });
    });

    // Watch for config JSON changes (aside sections may not fire standard events)
    var configObserver = new MutationObserver(function () {
      hydrate();
    });
    var configNode = document.getElementById('badge-config');
    if (configNode) {
      configObserver.observe(configNode, { childList: true, characterData: true, subtree: true });
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Live timer updates (every 60 s)                                    */
  /* ------------------------------------------------------------------ */

  setInterval(function () {
    if (timerBadgeEls.length === 0) return;
    var now = Date.now();
    timerBadgeEls.forEach(function (item) {
      var remaining = item.endDate.getTime() - now;
      if (remaining <= 0) {
        if (item.expiredAction === 'hide') {
          item.badge.style.display = 'none';
        } else {
          item.el.style.display = 'none';
        }
      } else {
        item.el.textContent = formatCountdown(remaining, item.format);
      }
    });
  }, 60000);
})();
